import { z } from "zod";

/**
 * Shared enums. Every value here mirrors an ARCHITECTURE.md §2 `text({ enum })`
 * column, a Toss OpenAPI field, or a state-machine label. Literal casing is
 * significant: Toss order fields are UPPERCASE; internal lifecycle labels are
 * kept exactly as the spec writes them.
 */

// ── orders (Toss OpenAPI facing) ────────────────────────────────
export const OrderSideSchema = z.enum(["BUY", "SELL"]);
export type OrderSide = z.infer<typeof OrderSideSchema>;

export const OrderTypeSchema = z.enum(["MARKET", "LIMIT"]);
export type OrderType = z.infer<typeof OrderTypeSchema>;

/** Toss `timeInForce`: DAY (regular) | CLS (closing auction). */
export const TimeInForceSchema = z.enum(["DAY", "CLS"]);
export type TimeInForce = z.infer<typeof TimeInForceSchema>;

/**
 * `orders.status` — Toss lifecycle mirror plus the local-only `intent` value
 * written by the idempotent-order flow (§4.4) before the network call.
 */
export const OrderStatusSchema = z.enum([
  "intent",
  "PENDING",
  "PARTIAL_FILLED",
  "PENDING_CANCEL",
  "PENDING_REPLACE",
  "FILLED",
  "CANCELED",
  "REJECTED",
  "REPLACED",
  "CANCEL_REJECTED",
  "REPLACE_REJECTED",
]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

/** `getOrders` status filter group. */
export const OrderStatusGroupSchema = z.enum(["OPEN", "CLOSED"]);
export type OrderStatusGroup = z.infer<typeof OrderStatusGroupSchema>;

export const MarketCountrySchema = z.enum(["KR", "US"]);
export type MarketCountry = z.infer<typeof MarketCountrySchema>;

/** Market-calendar path param. */
export const MarketSchema = z.enum(["KR", "US"]);
export type Market = z.infer<typeof MarketSchema>;

/** Toss candle intervals — only 1m and 1d exist. */
export const CandleIntervalSchema = z.enum(["1m", "1d"]);
export type CandleInterval = z.infer<typeof CandleIntervalSchema>;

// ── strategies & runs ───────────────────────────────────────────
export const StrategyStatusSchema = z.enum(["draft", "active", "paused", "halted", "archived"]);
export type StrategyStatus = z.infer<typeof StrategyStatusSchema>;

/** `strategies.execution_mode` (§2). */
export const ExecutionModeSchema = z.enum(["auto", "approval-required", "ai-advisor"]);
export type ExecutionMode = z.infer<typeof ExecutionModeSchema>;

/** `strategy_runs.route` — the path a given run actually took (§2/§5). */
export const RunRouteSchema = z.enum(["auto", "approval", "ai-advisor"]);
export type RunRoute = z.infer<typeof RunRouteSchema>;

/** `strategy_runs.state` machine (§2, §5, §6). */
export const RunStateSchema = z.enum([
  "SCHEDULED",
  "EVALUATING",
  "NO_SIGNAL",
  "BLOCKED_RISK",
  "PENDING_APPROVAL",
  "PENDING_AI",
  "EXECUTING",
  "DONE",
]);
export type RunState = z.infer<typeof RunStateSchema>;

/** Terminal-ish `strategy_runs.outcome`. */
export const RunOutcomeSchema = z.enum([
  "no_signal",
  "blocked_risk",
  "filled",
  "partial",
  "rejected",
  "skipped",
  "vetoed",
  "expired",
  "error",
]);
export type RunOutcome = z.infer<typeof RunOutcomeSchema>;

/** `decisions.phase`. */
export const DecisionPhaseSchema = z.enum(["evaluate", "risk", "route", "execute"]);
export type DecisionPhase = z.infer<typeof DecisionPhaseSchema>;

// ── approvals & advisor ─────────────────────────────────────────
export const ApprovalStatusSchema = z.enum(["pending", "approved", "rejected", "expired"]);
export type ApprovalStatus = z.infer<typeof ApprovalStatusSchema>;

export const ApprovalOnTimeoutSchema = z.enum(["cancel", "auto-approve"]);
export type ApprovalOnTimeout = z.infer<typeof ApprovalOnTimeoutSchema>;

/** AI advisor gate verdict (§6). `resize` only ever shrinks. */
export const AdvisorVerdictSchema = z.enum(["approve", "veto", "resize"]);
export type AdvisorVerdict = z.infer<typeof AdvisorVerdictSchema>;

// ── notifications ───────────────────────────────────────────────
export const NotificationEventTypeSchema = z.enum([
  "signal",
  "orderPlaced",
  "orderFilled",
  "approvalRequested",
  "aiVerdict",
  "riskBreach",
  "error",
]);
export type NotificationEventType = z.infer<typeof NotificationEventTypeSchema>;

export const NotificationChannelSchema = z.enum(["push", "email", "web"]);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

export const NotificationStatusSchema = z.enum(["queued", "sent", "suppressed", "failed", "read"]);
export type NotificationStatus = z.infer<typeof NotificationStatusSchema>;

export const NotificationPrioritySchema = z.enum(["low", "normal", "high"]);
export type NotificationPriority = z.infer<typeof NotificationPrioritySchema>;

// ── credentials, features, auth ─────────────────────────────────
export const CredentialKindSchema = z.enum([
  "toss",
  "ai_openai",
  "ai_anthropic",
  // Generic OpenAI-compatible endpoint (OpenRouter / Ollama / LM Studio / vLLM).
  // Stored as an `api_key` credential carrying a required base URL (§6).
  "ai_custom",
  "push_expo",
]);
export type CredentialKind = z.infer<typeof CredentialKindSchema>;

export const CredentialAuthTypeSchema = z.enum(["api_key", "oauth_bearer", "client_secret"]);
export type CredentialAuthType = z.infer<typeof CredentialAuthTypeSchema>;

/** `credentials.verify_status`. */
export const VerifyStatusSchema = z.enum(["unverified", "verified", "failed"]);
export type VerifyStatus = z.infer<typeof VerifyStatusSchema>;

/** `feature_gates.feature` (§2). */
export const FeatureNameSchema = z.enum(["ai", "trading", "push"]);
export type FeatureName = z.infer<typeof FeatureNameSchema>;

/** `login_codes.purpose`. */
export const LoginCodePurposeSchema = z.enum(["claim", "login"]);
export type LoginCodePurpose = z.infer<typeof LoginCodePurposeSchema>;

// ── portfolio & cashflow ────────────────────────────────────────
export const CashflowKindSchema = z.enum([
  "deposit",
  "withdrawal",
  "dividend",
  "fee",
  "interest",
  "tax",
]);
export type CashflowKind = z.infer<typeof CashflowKindSchema>;

export const CashflowDetectedBySchema = z.enum(["manual", "inferred"]);
export type CashflowDetectedBy = z.infer<typeof CashflowDetectedBySchema>;

export const SnapshotSourceSchema = z.enum(["close", "intraday", "post-fill", "manual"]);
export type SnapshotSource = z.infer<typeof SnapshotSourceSchema>;

/** Portfolio time-series window (`?period`, §8). */
export const PortfolioPeriodSchema = z.enum(["1d", "7d", "30d", "90d", "1y", "all"]);
export type PortfolioPeriod = z.infer<typeof PortfolioPeriodSchema>;

// ── chat & push ─────────────────────────────────────────────────
export const ChatRoleSchema = z.enum(["user", "assistant", "tool"]);
export type ChatRole = z.infer<typeof ChatRoleSchema>;

export const PushPlatformSchema = z.enum(["ios", "android"]);
export type PushPlatform = z.infer<typeof PushPlatformSchema>;
